import type { StatsConfig } from '@/components/composite/MediaViewer/components/MediaViewerStats/types';

import { mediaViewerBookmarksConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerBookmarks';
import { mediaViewerEngagementConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerEngagement';
import { mediaViewerImpressionsConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerImpressions';
import { mediaViewerLikesConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerLikes';
import { mediaViewerPostEngagementsConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerPostEngagements';
import { mediaViewerQuotesConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerQuotes';
import { mediaViewerRepliesReceivedConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerRepliesReceived';
import { mediaViewerRetweetsSimpleConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerRetweetsSimple';
import { mediaViewerVideoViewsQuartileConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerVideoViewsQuartile';
import { mediaViewerViewsConfig } from '@/lib/analytics/kpis/x/mvConfig/mediaViewerViews';
import { POST, TW_POST_TYPES } from '@/lib/posts/postTypes';

/**
 * Media Viewer KPI Configuration for X (Twitter) - Following M-Order from spec:
 * M-Order 1: Likes (with organic/promoted split)
 * M-Order 2: Replies (with organic/promoted split)
 * M-Order 3: Reposts (with organic/promoted split)
 * M-Order 4: Quote count
 * M-Order 5: Bookmark count
 * M-Order 7: Impressions (with organic/promoted split)
 * M-Order 8: Post Engagements (computed: totalLikes+totalReplies+totalReposts+totalQuotes+totalBookmarks)
 * M-Order 9: Engagement rate (Followers)
 * M-Order 10: Video views (with organic/promoted split) - VIDEO ONLY
 * M-Order 11: Video views quartile histogram (0%, 25%, 50%, 75%, 100%) - VIDEO ONLY
 *
 * Note: Organic/Promoted splits are displayed within the main KPI card,
 * not as separate KPI entries. The split data is fetched from {kpi_id}_organic
 * and {kpi_id}_paid fields in the analytics response.
 */

const BASIC_CONFIG: StatsConfig[] = [
    // M-Order 1: Likes (with organic/promoted split)
    mediaViewerLikesConfig,
    // M-Order 2: Replies (with organic/promoted split)
    mediaViewerRepliesReceivedConfig,
    // M-Order 3: Reposts (with organic/promoted split)
    mediaViewerRetweetsSimpleConfig,
    // M-Order 4: Quote count
    mediaViewerQuotesConfig,
    // M-Order 5: Bookmark count
    mediaViewerBookmarksConfig,
    // M-Order 7: Impressions (with organic/promoted split)
    mediaViewerImpressionsConfig,
    // M-Order 8: Post Engagements (computed total count)
    mediaViewerPostEngagementsConfig,
    // M-Order 9: Engagement rate (Followers)
    mediaViewerEngagementConfig
];

const VIDEO_CONFIG: StatsConfig[] = [
    // M-Order 1: Likes (with organic/promoted split)
    mediaViewerLikesConfig,
    // M-Order 2: Replies (with organic/promoted split)
    mediaViewerRepliesReceivedConfig,
    // M-Order 3: Reposts (with organic/promoted split)
    mediaViewerRetweetsSimpleConfig,
    // M-Order 4: Quote count
    mediaViewerQuotesConfig,
    // M-Order 5: Bookmark count
    mediaViewerBookmarksConfig,
    // M-Order 7: Impressions (with organic/promoted split)
    mediaViewerImpressionsConfig,
    // M-Order 8: Post Engagements (computed total count)
    mediaViewerPostEngagementsConfig,
    // M-Order 9: Engagement rate (Followers)
    mediaViewerEngagementConfig,
    // M-Order 10: Video views (with organic/promoted split)
    mediaViewerViewsConfig,
    // M-Order 11: Video views quartile histogram (0%, 25%, 50%, 75%, 100%)
    mediaViewerVideoViewsQuartileConfig
];

type MediaViewerStatsTwitterType = {
    [POST]: {
        [key: string]: StatsConfig[];
    };
};

export const MediaViewerStatsTwitter: MediaViewerStatsTwitterType = {
    [POST]: {
        [TW_POST_TYPES.TYPE_PHOTO]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_CAROUSEL]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_STATUS]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_RETWEET]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_RETWEET_COMMENT]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_LINK]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_REPLY]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_POLL]: BASIC_CONFIG,
        [TW_POST_TYPES.TYPE_VIDEO]: VIDEO_CONFIG
    }
};
